import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Event as EventIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { getSessionsAcceptingEnrollment, Session } from '../../services/sessionService';
import { getEnrollmentsByUser } from '../../services/enrollmentService';
import { useAuth } from '../../contexts/AuthContext';

function SessionsPage() {
  const { authState } = useAuth();
  const { user } = authState;
  
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [enrolledSessionIds, setEnrolledSessionIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        
        // Fetch all sessions that are open for enrollment
        const availableSessions = await getSessionsAcceptingEnrollment();
        
        // Filter out ended sessions and ensure basePrice is a number for all sessions
        const now = new Date();
        const processedSessions = availableSessions
          .filter(session => new Date(session.endDate) >= now)
          .map(session => ({
            ...session,
            basePrice: session.basePrice ? parseFloat(String(session.basePrice)) : 0
          }));

        // Get user's current enrollments to identify sessions they're already enrolled in
        if (user) {
          const enrollmentsData = await getEnrollmentsByUser(user.id);
          const sessionIds = enrollmentsData?.enrollments?.map(e => e.sessionId) || [];
          setEnrolledSessionIds(sessionIds);
        }
        
        setSessions(processedSessions);
      } catch (err) {
        console.error('Error fetching sessions:', err);
        setError('Failed to load available sessions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessions();
  }, [user]);
  
  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Available Sessions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse and enroll in our math learning sessions.
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress />
        </Box>
      ) : sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <CalendarIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Sessions Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            There are currently no sessions open for enrollment. Please check back later.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {sessions.map((session) => (
            <Grid item xs={12} md={6} lg={4} key={session.id}>
              <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {session.title}
                    </Typography>
                    <Chip 
                      icon={<EventIcon />} 
                      label="Open" 
                      color="success" 
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {`${new Date(session.startDate).toLocaleDateString()} - ${new Date(session.endDate).toLocaleDateString()}`}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body1" paragraph>
                    {session.description}
                  </Typography>

                  <Typography
                    variant="h6"
                    color={parseFloat(String(session.basePrice || 0)) === 0 ? "success.main" : "primary"}
                    sx={{ mt: 2, fontWeight: 'bold' }}
                  >
                    {parseFloat(String(session.basePrice || 0)) === 0
                      ? 'FREE'
                      : `$${parseFloat(String(session.basePrice)).toFixed(2)}`
                    }
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {enrolledSessionIds.includes(session.id) ? (
                    <Button 
                      variant="outlined" 
                      fullWidth
                      disabled
                    >
                      Already Enrolled
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      component={RouterLink}
                      to={`/sessions/${session.id}/enroll`}
                      fullWidth
                      endIcon={<ArrowForwardIcon />}
                    >
                      Enroll Now
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </MainLayout>
  );
}

export default SessionsPage;